import { useCachedData } from '../hooks/useCachedData';
import { endpoints } from '../api/config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  BUTTON_STYLE,
  DIALOG_CLOSE_BUTTON_STYLE,
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
  DIALOG_MAX_WIDTH,
  BUTTON_VARIANT,
  BUTTON_SIZE
} from '../constants/styles';

const Bins = () => {
  const { data, isLoading, error, refetch } = useCachedData(endpoints.bins);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bins-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [addBinOpen, setAddBinOpen] = useState(false);

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bins-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Define columns for the table
  const columns = useMemo(() => [
    { 
      accessorKey: 'id', 
      header: 'ID',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'bin_number', 
      header: 'Bin Number',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'location', 
      header: 'Location',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'memo', 
      header: 'Memo',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'bin_orientation', 
      header: 'Bin Orientation',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'aisle_no', 
      header: 'Aisle No',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'bin', 
      header: 'Bin',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'inactive_bin', 
      header: 'Inactive Bin',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'inventory_counted', 
      header: 'Inventory Counted',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'room', 
      header: 'Room',
      minSize: 200,
      size: 220,
      enableSorting: true
    },
    { 
      accessorKey: 'wh', 
      header: 'WH',
      minSize: 200,
      size: 220,
      enableSorting: true
    }
  ], []);

  // Transform the data to include numeric keys as IDs
  const tableData = useMemo(() => {
    if (!data) return [];
    
    // If data is an object with numeric keys
    if (typeof data === 'object' && !Array.isArray(data)) {
      return Object.entries(data).map(([key, value]) => ({
        id: key,
        ...value
      }));
    }
    
    return data;
  }, [data]);

  const appGrid = useDataGridProTable({
    data: tableData,
    loading: isLoading,
    error: error ? error.message || 'Failed to load bins' : null,
    title: 'Bins',
    columns,
    onDataRefresh: refetch,
    initialSorting: [{ id: 'id', desc: false }],
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    actionButtons: [
      <Button
        key="add-bin"
        variant={BUTTON_VARIANT}
        size={BUTTON_SIZE}
        sx={{
          ...BUTTON_STYLE,
          ml: 2,
        }}
        onClick={() => setAddBinOpen(true)}
      >
        Add Bin
      </Button>,
    ],
  });

  return (
    <DashboardLayout>
      <MuiXDataGridSurface hook={appGrid} DataGridPro={DataGridPro} />

      {/* Add Bin Dialog */}
      <Dialog 
        open={addBinOpen} 
        onClose={() => setAddBinOpen(false)} 
        maxWidth={DIALOG_MAX_WIDTH} 
        fullWidth
        disableEnforceFocus
        keepMounted
      >
        <DialogTitle>
          Add New Bin
          <IconButton
            aria-label="close"
            onClick={() => setAddBinOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <h2>Feature Available Soon</h2>
            <p>This feature is currently under development and will be available in a future update.</p>
          </Box>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Bins; 